import React, { useEffect, useRef } from 'react'
import { Box, Button, ButtonBase, Typography } from '@mui/material'
import moment from 'moment'
import { muahangKiotLike } from '../../../layout/muahangKiotTokens'
import { normalizeTime24h } from '../../../../utils/commonUtils'
import { TIME_SLOTS_15 } from './dateTimeUtils'

export type TimePicker24Props = {
  /** `HH:mm` 24h */
  value: string
  onChange: (hhmm: string) => void
  onClose: () => void
  /** Chọn slot / Bây giờ xong có đóng panel không. Mặc định true. */
  closeOnPick?: boolean
  /** Hiện nút Bây giờ / Xong. Mặc định true. */
  showActions?: boolean
  /**
   * Slot `HH:mm` nhỏ hơn mốc này không chọn được (`24:00` = khóa cả ngày).
   * Bằng mốc vẫn chọn được.
   */
  disabledBefore?: string
}

/**
 * Time picker 24h — chọn slot `00:00` → `23:45`, bước 15 phút.
 * Nút "Bây giờ" lấy đúng phút hiện tại (không làm tròn, không đụng ngày).
 */
export function TimePicker24({
  value,
  onChange,
  onClose,
  closeOnPick = true,
  showActions = true,
  disabledBefore,
}: TimePicker24Props) {
  const current = normalizeTime24h(value) || value || '00:00'
  const activeSlot = TIME_SLOTS_15.includes(current) ? current : ''
  const listRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>('[data-active="true"]')
    el?.scrollIntoView({ block: 'center' })
  }, [activeSlot])

  const pick = (slot: string) => {
    onChange(slot)
    if (closeOnPick) onClose()
  }

  return (
    <Box sx={{ width: 168, boxSizing: 'border-box' }}>
      <Box
        sx={{
          px: 1,
          py: 0.75,
          borderBottom: `1px solid ${muahangKiotLike.filterPanelBorder}`,
          bgcolor: '#f7f9fc',
          textAlign: 'center',
        }}
      >
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            color: muahangKiotLike.kiotTextMuted,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            mb: 0.15,
            fontFamily: muahangKiotLike.kiotFontStack,
          }}
        >
          Giờ (24h · 15p)
        </Typography>
        <Typography
          sx={{
            fontSize: 20,
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: '0.03em',
            color: muahangKiotLike.kiotTextPrimary,
            fontFamily: muahangKiotLike.kiotFontStack,
            lineHeight: 1.1,
          }}
        >
          {current}
        </Typography>
      </Box>

      <Box
        ref={listRef}
        sx={{
          maxHeight: 248,
          overflowY: 'auto',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '4px',
          p: 0.75,
          scrollBehavior: 'smooth',
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: 'rgba(15,23,42,0.18)',
            borderRadius: 8,
          },
        }}
      >
        {TIME_SLOTS_15.map((slot) => {
          const isActive = slot === activeSlot
          const blocked = disabledBefore != null && slot < disabledBefore
          return (
            <ButtonBase
              key={slot}
              data-active={isActive ? 'true' : undefined}
              disabled={blocked}
              onClick={() => {
                if (blocked) return
                pick(slot)
              }}
              sx={{
                minHeight: 28,
                borderRadius: '6px',
                border: `1px solid ${
                  isActive ? 'primary.main' : muahangKiotLike.filterPanelBorder
                }`,
                fontSize: 12,
                fontWeight: isActive ? 700 : 500,
                fontVariantNumeric: 'tabular-nums',
                fontFamily: muahangKiotLike.kiotFontStack,
                color: blocked
                  ? muahangKiotLike.kiotTextMuted
                  : isActive
                    ? '#fff'
                    : muahangKiotLike.kiotTextPrimary,
                bgcolor: isActive ? 'primary.main' : '#fff',
                letterSpacing: '0.01em',
                py: 0,
                opacity: blocked ? 0.38 : 1,
                transition: 'background-color 0.12s, border-color 0.12s',
                '&:hover': {
                  bgcolor: blocked || isActive
                    ? isActive
                      ? 'primary.dark'
                      : '#fff'
                    : 'rgba(15, 23, 42, 0.04)',
                  borderColor: isActive ? 'primary.dark' : 'rgba(15, 23, 42, 0.28)',
                },
                '&.Mui-disabled': {
                  opacity: 0.38,
                  color: muahangKiotLike.kiotTextMuted,
                },
              }}
            >
              {slot}
            </ButtonBase>
          )
        })}
      </Box>

      {showActions ? (
        <Box
          sx={{
            display: 'flex',
            gap: 0.75,
            p: 0.75,
            borderTop: `1px solid ${muahangKiotLike.filterPanelBorder}`,
          }}
        >
          <Button
            fullWidth
            size="small"
            variant="outlined"
            onClick={() => {
              const now = moment()
              const exact = `${String(now.hour()).padStart(2, '0')}:${String(now.minute()).padStart(2, '0')}`
              if (disabledBefore && exact < disabledBefore) return
              pick(exact)
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 12,
              minHeight: 30,
              py: 0.25,
              borderRadius: '6px',
              border: `1px solid ${muahangKiotLike.filterPanelBorder}`,
              fontFamily: muahangKiotLike.kiotFontStack,
            }}
          >
            Bây giờ
          </Button>
          <Button
            fullWidth
            size="small"
            variant="contained"
            onClick={onClose}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              fontSize: 12,
              minHeight: 30,
              py: 0.25,
              borderRadius: '6px',
              boxShadow: 'none',
              fontFamily: muahangKiotLike.kiotFontStack,
            }}
          >
            Xong
          </Button>
        </Box>
      ) : null}
    </Box>
  )
}

export default TimePicker24
